// Pure parser: GDACS getgeometry FeatureCollection -> normalised footprint for one event.
// No React / Leaflet imports so it can run under node --test.
import { parseGdacsIsoUtc, parseGdacsLabelTime } from './gdacsTime.js';
import { prepareGeometry, geometryCrossesAntimeridian, ringCentroid } from './geo.js';

export class GdacsParseError extends Error {
  constructor(message) {
    super(message);
    this.name = 'GdacsParseError';
  }
}

const EVENT_TYPES = ['TC', 'FL', 'EQ'];
const BAND_BY_CLASS = { Poly_Green: 60, Poly_Orange: 90, Poly_Red: 120 };
const SNAP_DEG = 0.05;
const CURRENT_WIND_TOLERANCE_MS = 3600 * 1000;

const round2 = (x) => Math.round(x * 100) / 100;
const ptKey = (p) => `${p[0].toFixed(4)},${p[1].toFixed(4)}`;
const isPoint = (p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]);
const lngDelta = (a, b) => {
  let d = Math.abs(a - b);
  if (d > 180) d = 360 - d;
  return d;
};
const distDeg = (a, b) => Math.hypot(lngDelta(a[0], b[0]), a[1] - b[1]);

// Natural order on Class ('Line_Line_2' before 'Line_Line_10'); makes the result independent
// of the order GDACS happens to serialise the features in.
const classOrder = (a, b) => {
  const ca = a.properties?.Class || '';
  const cb = b.properties?.Class || '';
  const ma = /^(.*?)(\d+)$/.exec(ca);
  const mb = /^(.*?)(\d+)$/.exec(cb);
  if (ma && mb && ma[1] === mb[1] && ma[2] !== mb[2]) return Number(ma[2]) - Number(mb[2]);
  if (ca !== cb) return ca < cb ? -1 : 1;
  const la = String(a.properties?.polygonlabel || '');
  const lb = String(b.properties?.polygonlabel || '');
  return la < lb ? -1 : la > lb ? 1 : 0;
};

const emptyTrack = () => ({
  segments: [],
  rawSegments: [],
  vertices: [],
  trackIncomplete: false,
  animatable: false,
  source: 'lines',
});

// Walks the unordered line pieces from the centroid outwards. Never invents points.
function chainTrack(rawLines, centroid) {
  const used = new Set();
  const byStart = new Map();
  const byEnd = new Map();
  const push = (m, k, v) => {
    if (!m.has(k)) m.set(k, []);
    m.get(k).push(v);
  };
  rawLines.forEach((s, i) => {
    push(byStart, ptKey(s.from), i);
    push(byEnd, ptKey(s.to), i);
  });

  let incomplete = false;
  let start = centroid;
  if (!byStart.has(ptKey(start)) && !byEnd.has(ptKey(start))) {
    let best = null;
    let bestD = SNAP_DEG;
    for (const s of rawLines) {
      for (const p of [s.from, s.to]) {
        const d = distDeg(p, centroid);
        if (d <= bestD) {
          bestD = d;
          best = p;
        }
      }
    }
    if (!best) return { past: [], forecast: [centroid], segments: [], incomplete: true };
    start = best;
  }

  const forward = [];
  let cursor = start;
  for (;;) {
    const open = (byStart.get(ptKey(cursor)) || []).filter((i) => !used.has(i));
    if (open.length === 0) break;
    if (open.length > 1) {
      incomplete = true;
      break;
    }
    used.add(open[0]);
    forward.push(rawLines[open[0]]);
    cursor = rawLines[open[0]].to;
  }

  const backward = [];
  cursor = start;
  for (;;) {
    const open = (byEnd.get(ptKey(cursor)) || []).filter((i) => !used.has(i));
    if (open.length === 0) break;
    if (open.length > 1) {
      incomplete = true;
      break;
    }
    used.add(open[0]);
    backward.unshift(rawLines[open[0]]);
    cursor = rawLines[open[0]].from;
  }

  if (used.size !== rawLines.length) incomplete = true;

  const past = backward.length ? [backward[0].from, ...backward.map((s) => s.to)] : [start];
  const forecast = [start, ...forward.map((s) => s.to)];
  const segments = [
    ...backward.map((s) => ({ ...s, phase: 'past' })),
    ...forward.map((s) => ({ ...s, phase: 'forecast' })),
  ];
  return { past, forecast, segments, incomplete };
}

// Timed-only advisories ship no Line_Line pieces. The small Point_Polygon_Point circles are centred on the
// published positions and labelled with their time, so the track is rebuilt from those centres.
function positionsTrack(pointPolys, centroid, anchorMs) {
  const positions = pointPolys
    .map((f) => {
      const prepared = prepareGeometry(f.geometry);
      const c = prepared ? ringCentroid(prepared.polys[0].outer) : null;
      const timeMs = parseGdacsLabelTime(f.properties?.polygonlabel, anchorMs);
      if (!c || timeMs == null) return null;
      return { lng: round2(c[0] > 180 ? c[0] - 360 : c[0]), lat: round2(c[1]), timeMs };
    })
    .filter(Boolean)
    .sort((a, b) => a.timeMs - b.timeMs)
    .filter((p, i, arr) => i === 0 || p.timeMs !== arr[i - 1].timeMs);
  if (positions.length < 2) return null;

  let split = -1;
  let bestD = SNAP_DEG;
  positions.forEach((p, i) => {
    const d = distDeg([p.lng, p.lat], centroid);
    if (d <= bestD) {
      bestD = d;
      split = i;
    }
  });
  if (split < 0) {
    const advisory = Math.min(...pointPolys.map((f) => parseGdacsIsoUtc(f.properties?.polygondate)).filter((n) => n != null));
    positions.forEach((p, i) => {
      if (p.timeMs <= advisory) split = i;
    });
    if (split < 0) split = 0;
  }

  const pairs = positions.map((p) => [p.lng, p.lat]);
  const segments = pairs.slice(1).map((to, i) => ({
    from: pairs[i], to, cls: 'UNK', phase: i < split ? 'past' : 'forecast',
  }));
  const vertices = positions.map((p, index) => ({
    lng: p.lng, lat: p.lat, timeMs: p.timeMs, phase: index < split ? 'past' : 'forecast', cls: 'UNK',
  }));
  return { segments, vertices };
}

// A track (line pieces, or the centres of the timed position circles) that jumps more than 180 degrees of longitude.
function trackCrossesAntimeridian(features) {
  const lines = features.filter((f) => f.geometry?.type === 'LineString' || f.geometry?.type === 'MultiLineString');
  const jumps = (coords) => coords.some((c, i) => i > 0 && isPoint(c) && isPoint(coords[i - 1]) && Math.abs(c[0] - coords[i - 1][0]) > 180);
  if (lines.some((f) => (f.geometry.type === 'LineString' ? [f.geometry.coordinates] : f.geometry.coordinates).some((cs) => Array.isArray(cs) && jumps(cs)))) return true;
  const centres = features
    .filter((f) => /^Point_Polygon_Point_\d+$/.test(f.properties?.Class || f.properties?.polygonclass || ''))
    .map((f) => prepareGeometry(f.geometry))
    .filter(Boolean)
    .map((p) => (p.bbox[0] + p.bbox[2]) / 2);
  return centres.length > 1 && Math.max(...centres) - Math.min(...centres) > 180;
}

export function parseGdacsGeometry(featureCollection, { eventType, fromDate, toDate, fallbackCentroid } = {}) {
  if (!featureCollection || typeof featureCollection !== 'object' || !Array.isArray(featureCollection.features)) {
    throw new GdacsParseError('GDACS geometry is not a FeatureCollection');
  }
  if (!EVENT_TYPES.includes(eventType)) {
    throw new GdacsParseError(`Unsupported GDACS event type: ${eventType}`);
  }

  const features = featureCollection.features
    .filter((f) => f && typeof f === 'object')
    .sort(classOrder);
  // One longitude frame for the whole event: if any polygon crosses the antimeridian, every polygon is stored shifted.
  const frameShifted = features.some((f) => geometryCrossesAntimeridian(f.geometry)) || trackCrossesAntimeridian(features);
  const prep = (g) => prepareGeometry(g, frameShifted);
  const anchorMs = parseGdacsIsoUtc(fromDate) ?? parseGdacsIsoUtc(toDate) ?? Date.now();

  let centroid = null;
  let coneFeature = null;
  const rawLines = [];
  const rawPieces = [];
  let linesIncomplete = false;
  const pointPolys = [];
  const windSwath = {};
  const windMap = new Map();
  let flood = null;
  let quake = null;
  const quakeLabels = new Set();

  for (const f of features) {
    const props = f.properties || {};
    const cls = props.Class;
    const geom = f.geometry;
    if (cls === 'Point_Centroid') {
      if (isPoint(geom?.coordinates)) centroid = [geom.coordinates[0], geom.coordinates[1]];
    } else if (/^Line_Line_\d+$/.test(cls || '')) {
      const coords = Array.isArray(geom?.coordinates) ? geom.coordinates.filter(isPoint) : [];
      const lineCls = ['HU', 'TS', 'TD'].includes(props.polygonlabel) ? props.polygonlabel : 'UNK';
      if (coords.length !== 2) linesIncomplete = true;
      if (coords.length >= 2) {
        for (let i = 0; i + 1 < coords.length; i += 1) {
          rawPieces.push({ from: coords[i], to: coords[i + 1], cls: lineCls });
        }
        if (coords.length === 2) rawLines.push({ from: coords[0], to: coords[1], cls: lineCls });
      }
    } else if (/^Point_Polygon_Point_\d+$/.test(cls || '')) {
      pointPolys.push(f);
    } else if (cls in BAND_BY_CLASS) {
      const band = BAND_BY_CLASS[cls];
      const label = String(props.polygonlabel || '');
      const prepared = prep(geom);
      if (/km\/h/.test(label)) {
        if (prepared) windSwath[band] = prepared;
      } else {
        const timeMs = parseGdacsIsoUtc(props.polygondate) ?? parseGdacsLabelTime(label, anchorMs);
        if (timeMs != null) {
          if (!windMap.has(timeMs)) windMap.set(timeMs, { timeMs, sets: {} });
          if (prepared) windMap.get(timeMs).sets[band] = prepared;
        }
      }
    } else if (cls === 'Poly_Cones') {
      coneFeature = f;
    } else if (cls === 'Poly_Affected') {
      const prepared = prep(geom);
      if (prepared && eventType === 'FL') flood = { affected: prepared };
    } else if (cls === 'Poly_Circle') {
      const prepared = prep(geom);
      if (prepared && eventType === 'EQ') quake = { ...(quake || { bands: [] }), circle: prepared };
    } else if (/^Poly_SMPInt_\d+$/.test(cls || '')) {
      const label = String(props.polygonlabel || '');
      const intensity = Number(/[\d.]+/.exec(label)?.[0] ?? props.intensity);
      const prepared = prep(geom);
      if (eventType === 'EQ' && prepared && Number.isFinite(intensity) && !quakeLabels.has(label)) {
        quakeLabels.add(label);
        quake = quake || { bands: [] };
        quake.bands.push({ intensity, prepared });
      }
    }
  }

  if (quake) {
    quake.bands.sort((a, b) => b.intensity - a.intensity);
    quake.circle = quake.circle || null;
  }

  let trackIncomplete = linesIncomplete;
  if (!centroid) {
    if (isPoint(fallbackCentroid)) centroid = [fallbackCentroid[0], fallbackCentroid[1]];
    trackIncomplete = true;
  }

  const track = emptyTrack();
  track.rawSegments = rawPieces;

  if (eventType === 'TC' && centroid) {
    const rebuilt = rawLines.length === 0 && pointPolys.length >= 2 ? positionsTrack(pointPolys, centroid, anchorMs) : null;
    const chain = rebuilt || chainTrack(rawLines, centroid);
    if (chain.incomplete) trackIncomplete = true;
    const { past, forecast, segments } = chain;
    track.segments = segments;

    if (rebuilt) {
      // Positions already carry their time: no snapping against chained vertices.
      track.rawSegments = rebuilt.segments;
      track.vertices = rebuilt.vertices;
      track.source = 'positions';
    } else {
      // Vertices: past then forecast, the centroid appears once (end of past / start of forecast).
      const pastOnly = past.length && forecast.length && ptKey(past[past.length - 1]) === ptKey(forecast[0])
        ? past.slice(0, -1)
        : past;
      const classAt = (pt, side) => {
        const k = ptKey(pt);
        const seg = side === 'end'
          ? segments.find((s) => ptKey(s.to) === k)
          : segments.find((s) => ptKey(s.from) === k);
        return seg ? seg.cls : null;
      };
      const list = [
        ...pastOnly.map((p, i) => ({
          lng: p[0], lat: p[1], timeMs: null, phase: 'past',
          cls: (i === 0 ? classAt(p, 'start') : classAt(p, 'end')) || 'UNK',
        })),
        ...forecast.map((p, i) => ({
          lng: p[0], lat: p[1], timeMs: null, phase: 'forecast',
          cls: (i === 0 ? classAt(p, 'start') || classAt(p, 'end') : classAt(p, 'end')) || 'UNK',
        })),
      ];
      track.vertices = list;

      // Times come from the small PointRadii polygons: label = vertex time, polygon centre = vertex.
      for (const f of pointPolys) {
        const label = f.properties?.polygonlabel;
        const prepared = prep(f.geometry);
        const timeMs = parseGdacsLabelTime(label, anchorMs);
        const c = prepared ? ringCentroid(prepared.polys[0].outer) : null;
        if (!c || timeMs == null) continue;
        const centre = [c[0] > 180 ? c[0] - 360 : c[0], c[1]];
        let best = null;
        let bestD = SNAP_DEG;
        for (const v of track.vertices) {
          const d = distDeg([v.lng, v.lat], centre);
          if (d <= bestD) {
            bestD = d;
            best = v;
          }
        }
        if (!best || best.timeMs != null) continue;
        best.timeMs = timeMs;
      }
    }

    const timedForecast = track.vertices.filter((v) => v.phase === 'forecast' && v.timeMs != null);
    track.animatable = !trackIncomplete
      && timedForecast.length >= 2
      && timedForecast.every((v, i) => i === 0 || v.timeMs > timedForecast[i - 1].timeMs);
  }
  track.trackIncomplete = trackIncomplete;

  const modifiedMs = parseGdacsIsoUtc(features.find((f) => f.properties?.datemodified)?.properties.datemodified);
  const windByTime = [...windMap.values()].sort((a, b) => a.timeMs - b.timeMs);

  // The first forecast vertex is the centroid, i.e. the advisory position.
  const centroidVertex = track.vertices.find((v) => v.phase === 'forecast');
  let currentWindIndex = null;
  if (centroidVertex?.timeMs != null) {
    const idx = windByTime.findIndex((w) => Math.abs(w.timeMs - centroidVertex.timeMs) <= CURRENT_WIND_TOLERANCE_MS);
    currentWindIndex = idx >= 0 ? idx : null;
  }

  return {
    eventType,
    sourceUrl: null,
    centroid,
    track,
    cone: coneFeature ? prep(coneFeature.geometry) : null,
    windSwath,
    windByTime,
    currentWindIndex,
    modifiedMs,
    flood,
    quake,
  };
}
